const STORAGE_KEY = 'admin-product-3d-scene-settings-v2';

const clamp = (value, min, max, fallback) => {
    const number = Number(value);

    if (!Number.isFinite(number)) {
        return fallback;
    }

    return Math.min(max, Math.max(min, number));
};

const normalizeBoolean = (value, fallback) => (
    typeof value === 'boolean' ? value : fallback
);

export const DEFAULT_ADMIN_PRODUCT_3D_SCENE_SETTINGS = Object.freeze({
    sunAngle: 135,
    sunVertical: 38,
    sunIntensity: 2.8,
    fillIntensity: 0.68,
    hemisphereIntensity: 0.32,
    ambientIntensity: 0.08,
    rimIntensity: 0.34,
    shadowsEnabled: true,
    graphicsQuality: 'medium',
    dimensionLabelScale: 1.35,
    backgroundMode: 'theme',
    backgroundTheme: 'light',
    backgroundUrl: '',
    backgroundAsEnvironment: true,
    showGround: true,
});

export const ADMIN_PRODUCT_3D_LIGHTING_PRESETS = Object.freeze([
    Object.freeze({
        value: 'balanced',
        label: 'Баланс',
        description: 'Одна тень, мягкая подсветка спереди и сзади.',
        settings: Object.freeze({
            sunAngle: 135,
            sunVertical: 38,
            sunIntensity: 2.8,
            fillIntensity: 0.68,
            hemisphereIntensity: 0.32,
            ambientIntensity: 0.08,
            rimIntensity: 0.34,
            shadowsEnabled: true,
        }),
    }),
    Object.freeze({
        value: 'soft',
        label: 'Мягкий',
        description: 'Меньше контраста и спокойнее обратная сторона.',
        settings: Object.freeze({
            sunAngle: 128,
            sunVertical: 46,
            sunIntensity: 2.25,
            fillIntensity: 0.9,
            hemisphereIntensity: 0.46,
            ambientIntensity: 0.12,
            rimIntensity: 0.2,
            shadowsEnabled: true,
        }),
    }),
    Object.freeze({
        value: 'relief',
        label: 'Рельеф',
        description: 'Сильнее подчёркивает фаски и фрезеровку.',
        settings: Object.freeze({
            sunAngle: 118,
            sunVertical: 24,
            sunIntensity: 3.35,
            fillIntensity: 0.44,
            hemisphereIntensity: 0.23,
            ambientIntensity: 0.05,
            rimIntensity: 0.62,
            shadowsEnabled: true,
        }),
    }),
]);

export const normalizeAdminProduct3DSceneSettings = (settings = {}) => {
    const source = (
        settings
        && typeof settings === 'object'
        && !Array.isArray(settings)
    ) ? settings : {};
    const defaults = DEFAULT_ADMIN_PRODUCT_3D_SCENE_SETTINGS;
    const graphicsQuality = ['low', 'medium', 'high'].includes(
        source.graphicsQuality
    ) ? source.graphicsQuality : defaults.graphicsQuality;
    const backgroundMode = source.backgroundMode === 'panorama'
        ? 'panorama'
        : 'theme';
    const backgroundTheme = ['light', 'studio', 'dark'].includes(
        source.backgroundTheme
    ) ? source.backgroundTheme : defaults.backgroundTheme;

    return {
        sunAngle: clamp(source.sunAngle, 0, 360, defaults.sunAngle),
        sunVertical: clamp(
            source.sunVertical,
            -20,
            89,
            defaults.sunVertical
        ),
        sunIntensity: clamp(
            source.sunIntensity,
            0,
            10,
            defaults.sunIntensity
        ),
        fillIntensity: clamp(
            source.fillIntensity,
            0,
            3,
            defaults.fillIntensity
        ),
        hemisphereIntensity: clamp(
            source.hemisphereIntensity,
            0,
            2,
            defaults.hemisphereIntensity
        ),
        ambientIntensity: clamp(
            source.ambientIntensity,
            0,
            1,
            defaults.ambientIntensity
        ),
        rimIntensity: clamp(
            source.rimIntensity,
            0,
            3,
            defaults.rimIntensity
        ),
        shadowsEnabled: normalizeBoolean(
            source.shadowsEnabled,
            defaults.shadowsEnabled
        ),
        graphicsQuality,
        dimensionLabelScale: clamp(
            source.dimensionLabelScale,
            0.8,
            3,
            defaults.dimensionLabelScale
        ),
        backgroundMode,
        backgroundTheme,
        backgroundUrl: String(source.backgroundUrl || '').trim(),
        backgroundAsEnvironment: normalizeBoolean(
            source.backgroundAsEnvironment,
            defaults.backgroundAsEnvironment
        ),
        showGround: normalizeBoolean(
            source.showGround,
            defaults.showGround
        ),
    };
};

export const getAdminProduct3DLightingPreset = (value) => (
    ADMIN_PRODUCT_3D_LIGHTING_PRESETS.find(
        (preset) => preset.value === value
    ) || ADMIN_PRODUCT_3D_LIGHTING_PRESETS[0]
);

export const getDefaultAdminProduct3DLightingPatch = () => {
    const defaults = DEFAULT_ADMIN_PRODUCT_3D_SCENE_SETTINGS;

    return {
        sunAngle: defaults.sunAngle,
        sunVertical: defaults.sunVertical,
        sunIntensity: defaults.sunIntensity,
        fillIntensity: defaults.fillIntensity,
        hemisphereIntensity: defaults.hemisphereIntensity,
        ambientIntensity: defaults.ambientIntensity,
        rimIntensity: defaults.rimIntensity,
        shadowsEnabled: defaults.shadowsEnabled,
    };
};

export const loadAdminProduct3DSceneSettings = () => {
    if (typeof window === 'undefined') {
        return {
            ...DEFAULT_ADMIN_PRODUCT_3D_SCENE_SETTINGS,
        };
    }

    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);

        if (!stored) {
            return {
                ...DEFAULT_ADMIN_PRODUCT_3D_SCENE_SETTINGS,
            };
        }

        return normalizeAdminProduct3DSceneSettings(
            JSON.parse(stored)
        );
    } catch (_error) {
        return {
            ...DEFAULT_ADMIN_PRODUCT_3D_SCENE_SETTINGS,
        };
    }
};

export const saveAdminProduct3DSceneSettings = (settings) => {
    if (typeof window === 'undefined') return;

    try {
        window.localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify(
                normalizeAdminProduct3DSceneSettings(settings)
            )
        );
    } catch (_error) {
        // Недоступный localStorage не должен ломать WebGL-сцену.
    }
};
